'use client';
import throttle from 'lodash.throttle';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SearchVilla, searchVillas } from '@/actions/villages';
import Button from '../Button';
import GlobalLoader from '../GlobalLoader';
import Input from '../Input';
import LoginModal from '../LoginModal';
import { SearchResultsContainer } from '../NewHeader/SearchResultsContainer';
import HeaderPortrait from './HeaderPortrait';
import HeaderSelect from './HeaderSelect';
import MenuContent from './MenuContent';
import MenuOptions from './MenuOptions';

import { useTranslation } from '@/contexts/dictionaryContext';
import { useNewHeader } from '@/featureFlags/newHeader';
import useGlobalContext from '@/hooks/useGlobalContext';
import { useLocale } from '@/hooks/useLocale';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';
import { VillasCount } from '@/services/types';
import { customLink } from '@/utils';
import { IoSearchSharp } from 'react-icons/io5';
import logoBlue from '../../../public/logo-desktop.svg';
import logoWhite from '../../../public/white-desktop-logo.svg';
import UserAvatar from '../UserAvatar';
import UserDropDown from '../UserDropDown';

interface IHeaderProps {
  regionVillasCount: VillasCount[];
}

const Header: FC<IHeaderProps> = (props) => {
  const newHeader = useNewHeader();
  const pathname = usePathname();

  if (pathname.includes('resetting') || pathname.includes('login')) {
    return null;
  }

  if (!newHeader) {
    return <OldHeader {...props} />;
  }

  return null;
};

const OldHeader: FC<IHeaderProps> = ({ regionVillasCount }) => {
  const router = useRouter();
  const pathname = usePathname();
  const lang = useLocale();
  const { header, modals, userDropDown } = useTranslation();

  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
  const [showVilla, setShowVilla] = useState<boolean>(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);

  const { isLogged } = useUserContext();
  const { isHeaderActive, setIsHeaderActive } = useGlobalContext();

  const searchInputRef = useRef<HTMLDivElement>(null);

  const { register, handleSubmit, setValue, watch } = useForm();
  const searchValue = (watch('search') ?? '').toString().trim();
  const currentRequestId = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [results, setResults] = useState<SearchVilla[]>([]);
  const [lastSearchedQuery, setLastSearchedQuery] = useState<string | null>(
    null,
  );

  const isHomePage = ['/', '/it', '/fr', '/de'].includes(pathname);

  const isActive = useMemo(
    () => (isHomePage ? isScrolled || isHeaderActive : true),
    [isHeaderActive, isHomePage, isScrolled],
  );

  const clearSearch = () => setValue('search', '');

  const handleSearchInput = () => {
    if (showSearchInput) {
      clearSearch();
    }
    setShowSearchInput(!showSearchInput);
    setIsHeaderActive(true);
  };

  // useEffect(() => {
  //   const handleClose = (event: MouseEvent) => {
  //     if (
  //       searchInputRef.current &&
  //       !searchInputRef.current.contains(event.target as Node)
  //     ) {
  //       setShowSearchInput(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClose);

  //   return () => {
  //     document.removeEventListener('mousedown', handleClose);
  //   };
  // }, [searchInputRef]);

  useEffect(() => {
    if (showSearchInput && searchInputRef.current) {
      searchInputRef.current.querySelector('input')?.focus();
    }
  }, [showSearchInput]);

  useEffect(() => {
    if (!showSearchInput && !openMenu) {
      setResults([]);
      setIsLoading(false);
      setHasError(false);
      setLastSearchedQuery(null);
      return;
    }

    if (searchValue.length < 3) {
      setResults([]);
      setIsLoading(false);
      setHasError(false);
      setLastSearchedQuery(null);
      return;
    }

    setLastSearchedQuery(null);
    setHasError(false);

    const timer = setTimeout(async () => {
      const requestId = ++currentRequestId.current;
      setIsLoading(true);

      try {
        const data = await searchVillas(searchValue);

        if (requestId !== currentRequestId.current) return;

        setResults(data.data);
        setLastSearchedQuery(searchValue);
      } catch {
        if (requestId !== currentRequestId.current) return;
        setResults([]);
        setLastSearchedQuery(searchValue);
        setHasError(true);
      } finally {
        if (requestId === currentRequestId.current) {
          setIsLoading(false);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [openMenu, searchValue, showSearchInput]);

  useEffect(() => {
    if (showVilla || showGuide) {
      setIsHeaderActive(true);
    } else if (!showVilla && !showGuide && !openMenu) {
      setIsHeaderActive(false);
    }
  }, [showVilla, showGuide, setIsHeaderActive, openMenu]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  return (
    <>
      <header
        className={cn(
          isActive ? 'bg-white text-black/90' : 'bg-custom-gradient text-white',
          'sticky top-0 flex desktop:h-[100px] items-center w-full px-0 desktop:px-12 desktop:max-w-full z-[999]',
          {
            'z-[1000]': openMenu,
          },
        )}>
        <div className="relative justify-between items-center w-full hidden desktop:flex desktop:shrink-0">
          <Link
            className="leading-[2] text-xl pt-1 left-20 top-7 z-[200] w-[225px]"
            href={customLink(lang, '/')}>
            <figure className="inline-block">
              <Image
                priority={true}
                src={isActive ? logoBlue : logoWhite}
                alt="logo"
                width={225}
                height={40}
              />
            </figure>
          </Link>

          <MenuOptions
            dict={header}
            lang={lang}
            pathname={pathname}
            setShowVilla={setShowVilla}
            setShowGuide={setShowGuide}
          />

          <div className="flex items-center">
            <HeaderSelect isActive={isActive} />
            <button
              id="search-villages"
              aria-label="Open village search input"
              className={cn(
                'cursor-pointer px-[13px] py-[10px] text-xl xl:px-[calc(0.9375rem+10px)] transition-colors duration-300 ease-in-out',
                isActive ? 'text-black/90' : 'text-white',
              )}
              onClick={handleSearchInput}>
              <IoSearchSharp />
            </button>
            {!isLogged ? (
              <LoginModal dict={modals} isHeader />
            ) : (
              <UserDropDown dict={userDropDown}>
                <UserAvatar.Container>
                  <UserAvatar.Root />
                </UserAvatar.Container>
              </UserDropDown>
            )}
          </div>
        </div>

        <HeaderPortrait
          setShowVilla={setShowVilla}
          setShowGuide={setShowGuide}
          register={register}
          isLoading={isLoading}
          hasError={hasError}
          results={results}
          searchValue={searchValue}
          lastSearchedQuery={lastSearchedQuery}
          isActive={isActive}
          openMenu={openMenu}
          setOpenMenu={setOpenMenu}
          clearSearch={clearSearch}
        />
      </header>
      <div
        ref={searchInputRef}
        className={cn(
          `fixed left-0 right-0 top-0 w-full h-[100px] bg-white shadow-lg z-[999] transition-all duration-500 ease-in-out`,
          showSearchInput
            ? 'block opacity-100 translate-y-0'
            : 'hidden opacity-0 -translate-y-4',
        )}>
        {showSearchInput && (
          <div className="container mx-auto h-full flex items-center justify-between px-12">
            <div className="w-[225px]">
              <figure className="inline-block">
                <Image
                  priority={true}
                  src={logoBlue}
                  alt="logo"
                  width={225}
                  height={40}
                />
              </figure>
            </div>
            <div className="desktop:max-w-[1200px] flex-1 flex mx-4">
              <Input.Container className="w-full relative">
                <Input.Root
                  register={register}
                  name="search"
                  placeholder={header.search}
                  className="placeholder:text-gray-400 placeholder:text-sm w-full"
                  autoFocus
                />
                <GlobalLoader
                  isLoading={isLoading}
                  className="!absolute !z-30 !w-full !h-[120px] !top-[44px] !left-0 !bg-white/80"
                />
                <SearchResultsContainer
                  isLoading={isLoading}
                  hasError={hasError}
                  results={results}
                  searchValue={searchValue}
                  lastSearchedQuery={lastSearchedQuery}
                  handleClose={() => setShowSearchInput(false)}
                />
              </Input.Container>

              <div className="flex items-center gap-2 pl-5">
                <Button
                  className="border-none w-auto h-[44px] px-[10px] bg-white text-gray-500"
                  onClick={() => {
                    setShowSearchInput(false);
                    setIsHeaderActive(false);
                    clearSearch();
                  }}>
                  <span className="uppercase text-sm font-medium">
                    {header.searchClose}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <MenuContent
        dict={header}
        lang={lang}
        showVilla={showVilla}
        showGuide={showGuide}
        setShowVilla={setShowVilla}
        setShowGuide={setShowGuide}
        regionVillasCount={regionVillasCount}
      />
    </>
  );
};

export default Header;
